import { useCallback } from 'react'

import { useModal } from '@circlefin/modal-router'
import { FullScreen } from '@modals/layout'
import { routes } from '@services/sections/modal/routes'
import { GraphQLErrorBoundary } from '@shared/components/errors'
import {
  BlockchainRecipientAddressesWithMetadataDocument,
  BlockchainRecipientAddressWithMetadataDocument,
  useSetBlockchainRecipientAddressBeneficiaryInfoMutation,
} from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import { BeneficiaryInfoForm } from './BeneficiaryInfo.Form'

import type { BeneficiaryInfoFormProps } from './BeneficiaryInfo.Form'

export interface BeneficiaryInfoProps {
  /**
   * Address Id.
   */
  addressId?: string
}

export const BeneficiaryInfo: React.FC<BeneficiaryInfoProps> = ({
  addressId,
}) => {
  const { t } = useTranslation('address-book/modals')
  const modal = useModal()

  const [setBeneficiaryInfo, { loading, error }] =
    useSetBlockchainRecipientAddressBeneficiaryInfoMutation({
      refetchQueries: [
        BlockchainRecipientAddressWithMetadataDocument,
        BlockchainRecipientAddressesWithMetadataDocument,
      ],
    })

  const handleSubmit: BeneficiaryInfoFormProps['onSubmit'] = useCallback(
    (values) => {
      void setBeneficiaryInfo({
        variables: {
          input: {
            id: addressId ?? '',
            ...values,
          },
        },
        onCompleted: () => {
          return modal.router.push({
            pathname: routes.account.addressBook.addNewAddress.success,
          })
        },
      })
    },
    [addressId, modal.router, setBeneficiaryInfo],
  )

  return (
    <FullScreen>
      <GraphQLErrorBoundary error={error}>
        <div className="flex flex-col items-center pb-6">
          <h2 className="type-h-section-md">
            {t('addAddress.beneficiaryInfo.title')}
          </h2>
          <h3 className="text-neutral-subtle type-body-base-bold">
            {/* TODO: Add subtitle once copy is finalized */}
          </h3>
        </div>
        <BeneficiaryInfoForm loading={loading} onSubmit={handleSubmit} />
      </GraphQLErrorBoundary>
    </FullScreen>
  )
}
